import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import ToolSelector from '../../components/ToolSelector';
import PageTutorial from '../../components/PageTutorial';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Plus, Edit, Trash2, Star, Check, X, Building2,
  Users as UsersIcon, Wrench, Award, Factory, Inbox, Search,
} from 'lucide-react';
import BrandMark from '../../components/BrandMark';

const CATEGORIES = [
  { key: 'vendor',       label: 'Vendors',       icon: Building2 },
  { key: 'consultant',   label: 'Consultants',   icon: UsersIcon },
  { key: 'service',      label: 'Services',      icon: Wrench },
  { key: 'manufacturer', label: 'Manufacturers', icon: Factory },
  { key: 'association',  label: 'Associations',  icon: Award },
];

const emptyForm = () => ({
  name: '', description: '', region: '', link: '', logo: '',
  category: 'vendor', services: '', contact_email: '', featured: false,
});

const AdminDirectory = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();

  const [partners, setPartners] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/partners`),
        axios.get(`${API}/admin/directory/applications`),
      ]);
      setPartners(pRes.data || []);
      setApplications(aRes.data?.applications || []);
    } catch (e) {
      toast.error('Failed to load directory data');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (partner) => {
    setEditing(partner);
    setForm({
      name: partner.name || '',
      description: partner.description || '',
      region: partner.region || '',
      link: partner.link || '',
      logo: partner.logo || '',
      category: partner.category || 'vendor',
      services: (partner.services || []).join(', '),
      contact_email: partner.contact_email || '',
      featured: !!partner.featured,
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      region: form.region.trim() || null,
      link: form.link.trim() || null,
      logo: form.logo.trim() || null,
      category: form.category,
      services: form.services.split(',').map(s => s.trim()).filter(Boolean),
      contact_email: form.contact_email.trim() || null,
      featured: !!form.featured,
    };
    try {
      if (editing) {
        await axios.put(`${API}/partners/${editing.id}`, payload);
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/partners`, payload);
        toast.success('Partner added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePartner = async (partner) => {
    if (!window.confirm(`Remove "${partner.name}" from the directory?`)) return;
    try {
      await axios.delete(`${API}/partners/${partner.id}`);
      toast.success('Partner removed');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const approveApplication = async (app) => {
    try {
      await axios.post(`${API}/admin/directory/applications/${app.id}/approve`);
      toast.success(`${app.business_name} added to the directory`);
      fetchAll();
    } catch (e) {
      toast.error('Approve failed');
    }
  };

  const rejectApplication = async (app) => {
    const reason = window.prompt('Reason for rejection (optional):', '');
    if (reason === null) return; // cancelled
    try {
      await axios.post(`${API}/admin/directory/applications/${app.id}/reject`, null, {
        params: { reason: reason || 'Not a fit' },
      });
      toast.success('Application rejected');
      fetchAll();
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  const filteredPartners = useMemo(() => {
    let list = partners;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        (p.services || []).some(s => s.toLowerCase().includes(q))
      );
    }
    return list.slice().sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [partners, query]);

  const pendingApps = applications.filter(a => a.status === 'pending');

  return (
    <div className="min-h-screen bg-background" data-testid="admin-directory-page">
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" className="-ml-3 font-semibold" onClick={() => navigate('/admin/portal')} data-testid="admindir-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Admin portal
          </Button>
          <BrandMark size="md" subtitle="Directory Admin" onClick={() => navigate('/admin/portal')} />
          <div className="w-[120px]" />
        </div>
      </header>

      <ToolSelector />

      <main className="container mx-auto px-6 py-10">
        <PageTutorial id="admin_directory" />
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="eyebrow">Community directory</span>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight mt-3">
              Manage <span className="text-amber-brand">listings</span>
            </h1>
            <p className="text-muted-foreground mt-1 max-w-lg">
              Add, edit and vet the vendors, consultants and services that appear in the public directory.
            </p>
          </div>
          <Button
            className="rounded-full font-semibold h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 w-full md:w-auto"
            onClick={openNew}
            data-testid="admindir-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New listing
          </Button>
        </div>

        <Tabs defaultValue="listings" className="w-full">
          <TabsList className="grid grid-cols-2 max-w-md mb-6 bg-muted/70" data-testid="admindir-tabs">
            <TabsTrigger value="listings" data-testid="tab-listings">
              <Building2 className="w-4 h-4 mr-2" /> Listings ({partners.length})
            </TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">
              <Inbox className="w-4 h-4 mr-2" /> Applications
              {pendingApps.length > 0 && (
                <Badge className="ml-2 bg-primary text-primary-foreground hover:bg-primary">{pendingApps.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* LISTINGS */}
          <TabsContent value="listings">
            <div className="pro-card p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-10 h-11"
                  placeholder="Search listings by name, description or service…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="admindir-search"
                />
              </div>
            </div>

            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : filteredPartners.length === 0 ? (
              <div className="pro-card p-12 text-center">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="font-heading text-lg font-bold">No listings yet</p>
                <p className="text-sm text-muted-foreground mt-1">Add your first partner or approve a pending application.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPartners.map((p) => {
                  const catMeta = CATEGORIES.find(c => c.key === (p.category || 'vendor')) || CATEGORIES[0];
                  const CatIcon = catMeta.icon;
                  return (
                    <div key={p.id} className="pro-card p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admindir-row-${p.id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-11 w-11 rounded-lg bg-navy text-primary flex items-center justify-center font-heading font-extrabold flex-shrink-0">
                          {p.logo ? <img src={p.logo} alt="" className="h-11 w-11 rounded-lg object-cover" /> : (p.name?.[0]?.toUpperCase() || '?')}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-heading font-bold text-base truncate">{p.name}</h3>
                            {p.featured && <Star className="w-3.5 h-3.5 text-primary fill-primary" />}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            <span className="inline-flex items-center gap-1">
                              <CatIcon className="w-3 h-3" /> {catMeta.label}
                            </span>
                            {p.region && <span>· {p.region}</span>}
                            {p.services?.length > 0 && <span>· {p.services.length} services</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="rounded-full font-semibold" onClick={() => openEdit(p)} data-testid={`admindir-edit-${p.id}`}>
                          <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:bg-destructive/10" onClick={() => deletePartner(p)} data-testid={`admindir-delete-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* APPLICATIONS */}
          <TabsContent value="applications">
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : applications.length === 0 ? (
              <div className="pro-card p-12 text-center">
                <Inbox className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="font-heading text-lg font-bold">No applications</p>
                <p className="text-sm text-muted-foreground mt-1">New submissions from /directory will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map((a) => (
                  <div key={a.id} className="pro-card p-5" data-testid={`admindir-app-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading font-bold text-base">{a.business_name}</h3>
                          <Badge className={
                            a.status === 'pending' ? 'bg-primary/10 text-primary hover:bg-primary/10'
                            : a.status === 'approved' ? 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10'
                            : 'bg-destructive/10 text-destructive hover:bg-destructive/10'
                          }>{a.status}</Badge>
                          <Badge variant="outline" className="capitalize">{a.category}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {a.contact_name} · {a.contact_email}
                          {a.region && <> · {a.region}</>}
                          {a.website && <> · <a href={a.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{a.website}</a></>}
                        </p>
                        <p className="text-sm mt-3">{a.description}</p>
                        {a.services?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {a.services.map(s => (
                              <span key={s} className="text-[11px] font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">{s}</span>
                            ))}
                          </div>
                        )}
                        <p className="text-[11px] text-muted-foreground mt-3">
                          Submitted {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
                        </p>
                      </div>
                      {a.status === 'pending' && (
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <Button size="sm" className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => approveApplication(a)} data-testid={`admindir-approve-${a.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full font-semibold text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => rejectApplication(a)} data-testid={`admindir-reject-${a.id}`}>
                            <X className="w-3.5 h-3.5 mr-1.5" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <span className="eyebrow">{editing ? 'Update' : 'New'}</span>
            <DialogTitle className="font-heading text-2xl font-bold mt-2 tracking-tight">
              {editing ? 'Edit listing' : 'New listing'}
            </DialogTitle>
            <DialogDescription>Every field maps to what members see on /directory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dir-form-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="dir-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Region</Label>
                <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. Pacific NW, USA" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Website</Label>
                <Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Contact email</Label>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@…" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Logo URL</Label>
                <Input value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="https://…" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Services (comma-separated)</Label>
                <Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder="Mylar bags, wholesale, custom printing" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full p-3 border border-border rounded-xl bg-transparent min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="dir-form-featured"
                />
                <span className="font-semibold">Featured listing</span>
                <span className="text-muted-foreground text-xs">(pinned to the top of the directory)</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-full font-semibold" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={saveForm}
              disabled={saving}
              data-testid="dir-form-save"
            >
              {saving ? 'Saving…' : (editing ? 'Save changes' : 'Add listing')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDirectory;
